import cn from 'classnames';
import {CSSProperties, ReactElement, useState} from 'react';
import {useTranslation} from 'react-i18next';
import styles from './Alert.module.scss';
import Modal, {FULL_OVERLAY_STYLE} from './Modal';
import PreventMobileBodyScroll from './PreventMobileBodyScroll';
import Text from './Text';
import TextButton from '../../ui/button/TextButton';
import {callIfFunc} from '../function';
import {ZIndex} from '../scss/util/variable';
import {ButtonSet, IPAlertOneConfirm, IPAlertTwoConfirm} from '../type/alert';

type IAlertProps = IAlertOneConfirm | IAlertTwoConfirm;
interface IPropsCommon {
  isOpen: boolean;
  confirmDisabled?: boolean;
  spaceClassName?: string;
}
type OmitProps = 'predefinedCallback' | 'isAliveWhenRouting';
interface IAlertOneConfirm
  extends Omit<IPAlertOneConfirm, OmitProps>,
    IPropsCommon {}
interface IAlertTwoConfirm
  extends Omit<IPAlertTwoConfirm, OmitProps>,
    IPropsCommon {}

export default function Alert({
  isOpen,
  title,
  titleClassName,
  desc,
  buttonSet = ButtonSet.Confirm,
  labelConfirm,
  labelClose,
  labelSecondConfirm,
  isForced = false,
  callback,
  isTextAlignCenter = true,
  callbackSecond,
  onRequestClose,
  isButtonVertical = false,
  confirmDisabled = false,
  spaceClassName = 'px-28',
  alertZIndex = ZIndex.Modal + 1,
  hasAlertMaxHeight = true,
}: IAlertProps) {
  const {t} = useTranslation();
  const [isAlertMinHeight, setIsAlertMinHeight] = useState(false);
  const [hasScroll, setHasScroll] = useState(false);

  function onRequestCloseWrapper(e?: any) {
    if (
      e &&
      'key' in e &&
      e.key === 'Escape' &&
      e.type === 'keydown' &&
      isForced
    ) {
      return;
    }
    onRequestClose && onRequestClose(true);
  }
  function getBtnConfirm(index: number, _callback: () => void) {
    return (
      <TextButton
        {...getDefaultBtnProps(index)}
        onClick={_callback}
        className="btn-white light:btn-black"
        textClassName={cn(
          confirmDisabled
            ? 's16-bold-grey02'
            : 's16-bold-black light:text-white'
        )}
        disabled={confirmDisabled}
        isActiveTextOpacity50
      >
        {callIfFunc(labelConfirm, t) || t(DEFAULT_LABEL_CONFIRM_KEY)}
      </TextButton>
    );
  }
  function getBtnSecond(index: number, _callback: () => void) {
    return (
      <TextButton
        {...getDefaultBtnProps(index)}
        onClick={_callback}
        className="btn-white light:btn-black"
        textClassName="s16-bold-black light:text-white"
        isActiveTextOpacity50
      >
        {callIfFunc(labelSecondConfirm, t)}
      </TextButton>
    );
  }
  function getBtnCancel(index: number, _onRequestClose: () => void) {
    return (
      <TextButton
        {...getDefaultBtnProps(index)}
        onClick={_onRequestClose}
        className="btn-grey-01-grey-06 light:btn-white-grey-06"
        textClassName="s16-bold-grey13"
        isActiveTextOpacity50
      >
        {callIfFunc(labelClose, t) || t(DEFAULT_LABEL_CLOSE_KEY)}
      </TextButton>
    );
  }
  function getDefaultBtnProps(index: number) {
    return {
      key: index,
      width: '100%',
      height: '50px',
    };
  }

  const buttons: ReactElement[] = [];
  if (buttonSet === ButtonSet.Confirm && callback) {
    buttons.push(getBtnConfirm(0, callback));
  } else if (buttonSet === ButtonSet.Cancel && onRequestClose) {
    buttons.push(getBtnCancel(0, onRequestClose));
  } else if (
    buttonSet === ButtonSet.CancelAndConfirm &&
    callback &&
    onRequestClose
  ) {
    buttons.push(getBtnCancel(0, onRequestClose));
    buttons.push(getBtnConfirm(1, callback));
  } else if (buttonSet === ButtonSet.TwoConfirm && callback && callbackSecond) {
    buttons.push(getBtnSecond(0, callbackSecond));
    buttons.push(getBtnConfirm(1, callback));
  } else if (
    buttonSet === ButtonSet.CancelAndTwoConfirm &&
    callback &&
    callbackSecond &&
    onRequestClose
  ) {
    buttons.push(getBtnCancel(0, onRequestClose));
    buttons.push(getBtnSecond(1, callbackSecond));
    buttons.push(getBtnConfirm(2, callback));
  }

  const getPaddingBottomCn = () => {
    if (isButtonVertical) {
      if (buttons.length === 3) {
        return !title && !desc
          ? 'pb-[220px]'
          : hasScroll
            ? 'pb-[212px]'
            : 'pb-[232px]';
      } else if (buttons.length === 2) {
        return hasScroll ? 'pb-[150px]' : 'pb-[172px]';
      }
    }

    return hasScroll ? 'pb-90' : 'pb-[112px]';
  };

  const isCustomTitle =
    typeof title !== 'string' && typeof title !== 'function';
  const isCustomDesc = typeof desc !== 'string' && typeof desc !== 'function';
  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onRequestCloseWrapper}
      contentStyle={MODAL_STYLE}
      overlayStyle={{...OVERLAY_STYLE, zIndex: alertZIndex}}
      shouldCloseOnOverlayClick={!isForced}
      className={{
        base: 'AlertModal__Content bg-grey-01 light:bg-background',
        afterOpen: 'AlertModal__Content--after-open',
        beforeClose: 'AlertModal__Content--before-close',
      }}
      contentRef={e => {
        if (e) {
          setIsAlertMinHeight(e.clientHeight <= MIN_HEIGHT_POPUP);
          setHasScroll(Math.floor(window.innerHeight * 0.8) <= e.clientHeight);
        }
      }}
    >
      <div
        className={cn(
          'relative lg:m-auto lg:w-default-max-width',
          !title && !desc && getPaddingBottomCn()
        )}
      >
        <PreventMobileBodyScroll
          minHeight={isAlertMinHeight ? `${MIN_HEIGHT_POPUP}px` : 'auto'}
          maxHeight={hasAlertMaxHeight ? '80vh' : '100%'}
          className="!overflow-hidden flex flex-col"
        >
          {title && (
            <div
              className={cn(
                {
                  'mt-37': isAlertMinHeight && desc,
                  'mt-32': !isAlertMinHeight && desc,
                  'mb-16': desc,
                  'pt-32 pb-[232px]':
                    !desc && isButtonVertical && buttons.length === 3,
                  'pt-32 pb-[172px]':
                    !desc && isButtonVertical && buttons.length === 2,
                  'h-[130px] flex items-center':
                    !desc &&
                    ((isButtonVertical && buttons.length === 1) ||
                      !isButtonVertical),
                  'justify-center': isTextAlignCenter,
                },
                spaceClassName
              )}
            >
              {isCustomTitle && title}
              {!isCustomTitle && (
                <Text
                  className={cn(
                    titleClassName || DEFAULT_ALERT_TITLE_TEXT_CN,
                    'break-all leading-[22px]',
                    isTextAlignCenter && 'text-center'
                  )}
                >
                  {callIfFunc(title, t)}
                </Text>
              )}
            </div>
          )}
          {desc && (
            <div
              onTouchMove={e => e.stopPropagation()}
              className={cn(
                isTextAlignCenter && 'text-center',
                getPaddingBottomCn(),
                'overflow-y-auto'
              )}
            >
              <div className={spaceClassName}>
                {isCustomDesc && desc}
                {!isCustomDesc && (
                  <Text
                    className={cn('s13-regular-grey08 leading-[18px]', {
                      'mt-19': !title,
                    })}
                  >
                    {callIfFunc(desc, t)}
                  </Text>
                )}
              </div>
            </div>
          )}
          <div></div>
        </PreventMobileBodyScroll>
        <div
          className={cn(
            'absolute w-full px-18 bottom-0 pb-30 flex left-0 z-1 bg-grey-01 light:bg-white',
            isButtonVertical && buttons.length > 1
              ? cn('flex-col-reverse', styles.buttonsVerticalWrap)
              : styles.buttonsWrap,
            'pt-10'
          )}
        >
          {buttons}
        </div>
      </div>
    </Modal>
  );
}

const MODAL_STYLE: CSSProperties = {
  left: 0,
  right: 0,
  top: 'none',
  bottom: 0,
  padding: 0,
  /**
   * 모바일에서 발생하는 이슈
   * pointer-events를 none으로 설정하면 클릭(터치), 상태(hover 등), 커서 욥션이 비활성화 됨
   * modal이 open된 상태에서 touchMove 이벤트가 발생하면 body에 pointer-events: none이 설정됨
   * pointer-events는 하위 요소로 상속되기 때문에 react-modal에도 이 스타일이 적용되어 클릭(터치)이 막혀서 문제가 된다.
   * react-modal 내에 사용하는 모든 하위요소의 pointerEvents 속성에 auto를 부여하면 정상적으로 클릭이벤트가 발생한다.
   * https://css-tricks.com/almanac/properties/p/pointer-events/
   */
  pointerEvents: 'auto',
  border: 'none',
  borderRadius: '24px 24px 0px 0px',
  width: '100%',
  overflow: 'hidden',
};

const OVERLAY_STYLE: CSSProperties = {
  ...FULL_OVERLAY_STYLE,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: 'rgba(var(--any-black), 0.7)',
  zIndex: ZIndex.Modal + 1,
};

const DEFAULT_LABEL_CLOSE_KEY = 'common_cancel';
export const DEFAULT_LABEL_CONFIRM_KEY = 'common_ok';

const MIN_HEIGHT_POPUP = 210;

export const DEFAULT_ALERT_TITLE_TEXT_CN = 's17-medium-white light:text-black';
